import React, { useCallback } from 'react';
import Modal from '~/components/Modal';
import { MdClose } from 'react-icons/md';
import FormFooter from '~/components/form/form-footer';
import { useForm } from '~/hooks';
import { FormGroup, FormSelect } from '~/components/form';

/**
 * @typedef LienItemValues
 * @property {number}   sectionId
 * @property {string}   titleOpinionId 'Your File Number'
 * @property {boolean}  addLienFlag
 * @property {[object]} data
 * @property {boolean}  editRecord
 * 
 * 
 * @typedef FormState
 * @property {LienItemValues} values The current form values
 */

/** @type {LienItemValues} */
const defaultState = {
    id: 0,
    sectionId: 0,
    titleOpinionId: 0,
    data: [],
    lienType: { label: '', value: '' },
    lienTypeOptions: [
        {
            label: 'Deed of Trust',
            value: 'Deed of Trust'
        },
        {
            label: 'Equity Line',
            value: 'Equity Line'
        },
        {
            label: 'Judgment',
            value: 'Judgment'
        },
        {
            label: 'Leases & Rents',
            value: 'Leases & Rents'
        },
        {
            label: 'UCC',
            value: 'UCC'
        },
        {
            label: 'Tax Lien',
            value: 'Tax Lien'
        },
        {
            label: 'Other',
            value: 'Other'
        }
    ],
    action: { label: '', value: '' },
    actionOptions: [
        {
            label: 'Cancelled/Released',
            value: 'Cancelled/Released'
        },
        {
            label: 'To Remain of Record',
            value: 'To Remain of Record'
        },
        {
            label: 'To Remain of Record and Subordinated',
            value: 'To Remain of Record and Subordinated'
        }
    ],
    otherLienType: '',
    trustee: '',
    inFavorOf: '',
    dated: null,
    recordDate: null,
    book: '',
    pageNo: '',
    countyRegistry: '',
    instrumentNo: '',
    amount: '',
    addLienFlag: false,
    editRecord: false
};

const loadData = async (values, props) => {
    let editMode = false;
    if (props.liensDetailData && Object.keys(props.liensDetailData).length > 0) {
        editMode = true;
    }
    return {
        ...values,
        ...props,
        recordedBooks: props.recordedBooks,
        lienType: editMode ? props.liensDetailData._data.lienType : { label: '', value: '' },
        otherLienType: editMode ? props.liensDetailData._data.otherLienType : '',
        action: editMode ? props.liensDetailData._data.action : { label: '', value: '' },
        trustee: editMode ? props.liensDetailData._data.trustee : '',
        inFavorOf: editMode ? props.liensDetailData._data.inFavorOf : '',
        dated: editMode ? props.liensDetailData._data.dated : null,
        recordDate: editMode ? props.liensDetailData._data.recordDate : null,
        book: editMode ? props.liensDetailData._data.book : '',
        pageNo: editMode ? props.liensDetailData._data.pageNo : '',
        countyRegistry: editMode ? props.liensDetailData._data.countyRegistry : '',
        instrumentNo: editMode ? props.liensDetailData._data.instrumentNo : '',
        amount: editMode ? props.liensDetailData._data.amount : '',
        sectionId: values ? values.sectionId : 0,
    };
};

const LienItem = (props) => {
    const onLoad = useCallback((values, props) => {
        return loadData(values, props);
    }, [props.isModalOpen]);

    const { state, fieldActions } = useForm({
        initialState: { ...defaultState },
        props,
        onLoad,
        debug: true
    });

    return (
        <Modal isModalOpen={props.isModalOpen} preventScroll className='w-3/4 h-3/4 bg-clip-border bg-white rounded-md'>
            <section className='h-full flex flex-col'>
                <header className='pt-3 pb-2 px-8 border-b flex items-center justify-between'>
                    <p className='font-semibold'>{props.headerTxt}</p>
                    <button
                        className='rounded-full p-2 hover:bg-gray-150 focus:outline-none'
                        onClick={props.onCancel}
                    >
                        <MdClose />
                    </button>
                </header>
                <div className='flex-grow overflow-y-scroll'>
                    <div className="px-8 py-4 grid grid-cols-4 grid-flow-row gap-x-2 gap-y-2 text-sm mb-2 ">
                        <FormSelect
                            label="Lien Type*"
                            type='select'
                            isSearchable
                            options={state.values.lienTypeOptions}
                            isClearable={false}
                            groupClassName="col-span-2"
                            path='lienType'
                            value={state.values.lienType}
                            error={state.errors.lienType}
                            onChange={(lienType) => {
                                fieldActions.onChange(lienType, 'lienType');
                                return fieldActions.onChange({ label: '...', value: '' }, 'requestedBy');
                            }}
                        />
                        <FormSelect
                            label="Action to be Taken*"
                            type='select'
                            isSearchable
                            options={state.values.actionOptions}
                            isClearable={false}
                            groupClassName="col-span-2"
                            path='action'
                            value={state.values.action}
                            error={state.errors.action}
                            onChange={(action) => {
                                fieldActions.onChange(action, 'action');
                                return fieldActions.onChange({ label: '...', value: '' }, 'requestedBy');
                            }}
                        />
                        {state.values.lienType?.value === 'Other' && (
                            <FormGroup
                                label="Other Lien Type"
                                groupClassName="col-span-2"
                                path='otherLienType'
                                required
                                value={state.values.otherLienType}
                                error={state.errors.otherLienType}
                                {...fieldActions}
                            />
                        )}
                        <FormGroup
                            label="Trustee"
                            groupClassName="col-span-2"
                            path='trustee'
                            value={state.values.trustee}
                            error={state.errors.trustee}
                            {...fieldActions}
                        />
                        <FormGroup
                            label="In Favor Of"
                            groupClassName="col-span-2"
                            path='inFavorOf'
                            value={state.values.inFavorOf}
                            error={state.errors.inFavorOf}
                            {...fieldActions}
                        />

                        <FormGroup
                            label="Dated"
                            type='date-picker'
                            name='dated'
                            timeInputLabel="Time:"
                            dateFormat="MM/dd/yyyy"
                            showTimeInput
                            groupClassName="col-span-2"
                            datePickerClasses="w-full"
                            path='dated'
                            value={state.values.dated}
                            error={state.errors.dated}
                            {...fieldActions}
                            onChange={async (dated) => {
                                fieldActions.onChange(dated, 'dated');
                                fieldActions.onChange({ label: '...', value: '' }, 'requestedBy');
                            }}
                        />

                        <FormGroup
                            label="Record Date"
                            type='date-picker'
                            name='recordDate'
                            timeInputLabel="Time:"
                            dateFormat="MM/dd/yyyy"
                            showTimeInput
                            groupClassName="col-span-2"
                            datePickerClasses="w-full"
                            path='recordDate'
                            value={state.values.recordDate}
                            error={state.errors.recordDate}
                            {...fieldActions}
                            onChange={async (recordDate) => {
                                fieldActions.onChange(recordDate, 'recordDate');
                                fieldActions.onChange({ label: '...', value: '' }, 'requestedBy');
                            }}
                        />
                        <FormGroup
                            label="Book"
                            groupClassName="col-span-2"
                            path='book'
                            value={state.values.book}
                            error={state.errors.book}
                            {...fieldActions}
                        />
                        <FormGroup
                            label="Page No."
                            groupClassName="col-span-2"
                            path='pageNo'
                            value={state.values.pageNo}
                            error={state.errors.pageNo}
                            {...fieldActions}
                        />
                        <FormGroup
                            label="County Registry"
                            groupClassName="col-span-2"
                            path='countyRegistry'
                            value={state.values.countyRegistry}
                            error={state.errors.countyRegistry}
                            {...fieldActions}
                        />
                        <FormGroup
                            label="Instrument No."
                            groupClassName="col-span-2"
                            path='instrumentNo'
                            value={state.values.instrumentNo}
                            error={state.errors.instrumentNo}
                            {...fieldActions}
                        />
                        <FormGroup
                            label="Amount"
                            groupClassName="col-span-2"
                            type='currency'
                            path='amount'
                            value={state.values.amount}
                            error={state.errors.amount}
                            {...fieldActions}
                        />
                    </div>
                    <div className="col-span-6 flex justify-end items-center">
                        <FormFooter
                            submitDisabled={state.values.lienType.value === 'Other' && state.values.otherLienType.length === 0}
                            cancelBtnTxt={'Cancel'}
                            submitBtnTxt={
                                props.editRecord ? 'Save' : 'Add'}
                            onSubmit={() => {
                                props.editRecord ?
                                    props.saveLien(state.values)
                                    :
                                    props.addLien(state.values);
                            }}
                            onCancel={props.onCancel}
                        />
                    </div>
                </div>
            </section>
        </Modal>
    );
};

export default LienItem;

